import React from 'react';
import { Input } from '@/components/ui/Input';
import { formatDateForInput } from '@/utils/format';
import type { MetaBudgetMode } from './MetaBudgetStep';

interface AdSetScheduleStepProps {
  budgetMode: MetaBudgetMode;
  startDate: string;
  /** Backend field name is `end_time` (vs campaign's `stop_time`).
   *  Component name reflects backend nomenclature. */
  endDate: string;
  /** Daily-budget only — controls whether endDate is rendered. Lifetime
   *  always shows endDate (Meta requires it). */
  hasEndDate: boolean;
  today: string;
  onChangeStartDate: (date: string) => void;
  onChangeEndDate: (date: string) => void;
  onChangeHasEndDate: (hasEndDate: boolean) => void;
  errors?: {
    startDate?: string;
    endDate?: string;
  };
}

/**
 * Step 3 — schedule.
 *
 * Functionally identical to `MetaScheduleStep` but parameter names align
 * with the backend ad-set model (`end_time`, not `stop_time`). Couldn't
 * reuse the campaign step directly without renaming — and renaming the
 * campaign step would propagate through the A4.3.1 surface for no UX
 * benefit. Two consumers, structurally similar; the third wizard
 * (`_19`) tells us if there's a `<WizardScheduleStep>` to extract.
 *
 * `stopDateMin = startDate + 1 day` mirrors `MetaScheduleStep.tsx`
 * verbatim — the local-date one-liner avoids the west-of-UTC off-by-one
 * that `toISOString().slice(0,10)` causes in the evening.
 */
export const AdSetScheduleStep: React.FC<AdSetScheduleStepProps> = ({
  budgetMode,
  startDate,
  endDate,
  hasEndDate,
  today,
  onChangeStartDate,
  onChangeEndDate,
  onChangeHasEndDate,
  errors,
}) => {
  const isLifetime = budgetMode === 'lifetime';
  const showEndDate = isLifetime || hasEndDate;

  const endDateMin = startDate
    ? (() => {
        const [y, m, d] = startDate.split('-').map(Number);
        const next = new Date(y, m - 1, d + 1);
        return formatDateForInput(next);
      })()
    : today;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Schedule</h3>
        <p className="text-sm text-gray-600">
          {isLifetime
            ? 'Lifetime budgets need both a start and end date.'
            : 'Start date is required. Add an optional end date if you want the ad set to stop automatically.'}
        </p>
      </div>

      <div>
        <label
          htmlFor="meta-ad-set-start-date"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Start date <span className="text-red-500" aria-label="required">*</span>
        </label>
        <Input
          id="meta-ad-set-start-date"
          type="date"
          value={startDate}
          min={today}
          onChange={(e) => onChangeStartDate(e.target.value)}
          aria-invalid={Boolean(errors?.startDate)}
          aria-describedby={
            errors?.startDate ? 'meta-ad-set-start-date-error' : undefined
          }
          required
        />
        {errors?.startDate && (
          <p
            id="meta-ad-set-start-date-error"
            role="alert"
            className="text-sm text-red-600 mt-1"
          >
            {errors.startDate}
          </p>
        )}
      </div>

      {!isLifetime && (
        <div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={hasEndDate}
              onChange={(e) => onChangeHasEndDate(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm font-medium text-gray-700">
              Set an end date
            </span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            Leave off to run the ad set indefinitely (you can pause or
            archive it later).
          </p>
        </div>
      )}

      {showEndDate && (
        <div>
          <label
            htmlFor="meta-ad-set-end-date"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            End date
            {isLifetime ? (
              <span className="text-red-500 ml-1" aria-label="required">*</span>
            ) : null}
          </label>
          <Input
            id="meta-ad-set-end-date"
            type="date"
            value={endDate}
            onChange={(e) => onChangeEndDate(e.target.value)}
            min={endDateMin}
            aria-invalid={Boolean(errors?.endDate)}
            aria-describedby={
              errors?.endDate ? 'meta-ad-set-end-date-error' : undefined
            }
            required={isLifetime}
          />
          {errors?.endDate && (
            <p
              id="meta-ad-set-end-date-error"
              role="alert"
              className="text-sm text-red-600 mt-1"
            >
              {errors.endDate}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
